import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import {
  getReferrerCode,
  clearReferrerCode,
  getPartnerSlug,
  clearPartnerSlug,
} from "@/lib/useReferralTracker";

const CLIENT_ID = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID || "";

export default function GoogleSignInButton({ theme = "outline", size = "large", text = "continue_with" }) {
  const ref = useRef(null);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [message, setMessage] = useState(null); // { kind: "info" | "error", text }

  useEffect(() => {
    if (!CLIENT_ID) {
      console.error("REACT_APP_GOOGLE_OAUTH_CLIENT_ID is not set");
      return;
    }
    let cancelled = false;
    const waitForGsi = () =>
      new Promise((resolve) => {
        const tick = () => {
          if (cancelled) return;
          if (window.google?.accounts?.id) return resolve(window.google.accounts.id);
          setTimeout(tick, 50);
        };
        tick();
      });

    waitForGsi().then((gid) => {
      if (cancelled || !ref.current) return;
      gid.initialize({
        client_id: CLIENT_ID,
        callback: async ({ credential }) => {
          try {
            const ref_code = getReferrerCode() || undefined;
            const ref_partner_slug = getPartnerSlug() || undefined;
            const { data } = await api.post("/auth/google", {
              credential,
              ref_code,
              ref_partner_slug,
            });
            if (ref_code) clearReferrerCode();
            if (ref_partner_slug) clearPartnerSlug();
            if (data?.user) setUser(data.user);
            const u = data?.user;
            let dest = "/dashboard";
            if (u && !u.is_admin && u.role === "deals_user") {
              // deals_user role only — partner_admins get the regular
              // dashboard with extra nav entries for Deals and their
              // referral portal.
              dest = "/admin/deals";
            }
            navigate(dest, { replace: true });
          } catch (e) {
            const detail = e?.response?.data?.detail;
            const status = e?.response?.status;
            if (status === 403 && detail === "account_not_found") {
              setMessage({
                kind: "info",
                title: "We couldn't find an account for this email.",
                text: "Access to Reservoir AI is currently invitation-only. To request access, please join the waitlist and a member of our team will be in touch.",
                linkUrl: "https://myreservoir.ai/#waitlist",
                linkText: "Join the waitlist",
              });
            } else if (status === 403 && detail === "waitlist_pending") {
              setMessage({
                kind: "info",
                title: "Your access is pending review.",
                text: "Thank you for joining the waitlist. We'll email you as soon as your account has been approved.",
              });
            } else {
              setMessage({
                kind: "error",
                text: detail || e?.message || "Sign-in failed. Please try again.",
              });
            }
          }
        },
      });
      gid.renderButton(ref.current, {
        theme,
        size,
        text,
        shape: "rectangular",
        logo_alignment: "left",
        width: ref.current.offsetWidth || 320,
      });
    });
    return () => {
      cancelled = true;
    };
  }, [navigate, setUser, theme, size, text]);

  return (
    <div className="w-full">
      <div ref={ref} data-testid="google-signin-btn" className="w-full flex justify-center" />
      {message && (
        <div
          className="mt-3 text-[12.5px] p-3 rounded-[8px] leading-relaxed"
          style={
            message.kind === "info"
              ? { background: "rgba(15, 23, 42, 0.04)", color: "#0f172a", border: "1px solid rgba(15, 23, 42, 0.12)" }
              : { background: "var(--bad-bg)", color: "var(--bad)" }
          }
          data-testid="google-signin-message"
        >
          {message.title && (
            <div className="font-semibold mb-1">{message.title}</div>
          )}
          <div>{message.text}</div>
          {message.linkUrl && (
            <a
              href={message.linkUrl}
              className="inline-block mt-2 font-semibold underline underline-offset-2"
              data-testid="google-signin-message-link"
            >
              {message.linkText || message.linkUrl} →
            </a>
          )}
        </div>
      )}
    </div>
  );
}
